/**
 * ATL-1966 — a supply-order line, in the two shapes it is stored in.
 *
 * Firestore stores each line as the PRODUCT the merchant ordered, whole (`getProductsBySupplierDict`
 * → `{...product, orderQuantity}`): `id`, `name`, `storeStock`, `productUnit`, `purchasePrice`, … plus
 * the order's own `orderQuantity`. Until ATL-1966 the dual-write reduced each line to
 * `{productId, productName, quantity, price}`, and an organization reading Supabase reads those
 * four-key lines back until the backfill restores them (104 lines, 12 orders on 2026-10-31).
 *
 * On a full line `quantity` is the PRODUCT's own field, not the ordered quantity, so a line's facts
 * are read by shape — never `orderQuantity ?? quantity` across shapes. The backend reads the same way
 * (`apps/atlas-backend/src/modules/supply-orders/supply-order-line.util.ts`).
 */
import {readOrderQuantity, toSavedOrderItems} from './supplyOrderQuantity'

/**
 * A line in the Firestore shape: the one SupplyOrderDetailScreen can show (`storeStock`) and save
 * (`toSavedOrderItems` keeps only lines that carry an `orderQuantity`).
 *
 * @param {unknown} line
 * @returns {boolean}
 */
export const isFullSupplyOrderLine = (line) =>
  line !== null && typeof line === 'object' && Object.prototype.hasOwnProperty.call(line, 'orderQuantity')

/**
 * The product a line orders and the quantity on the way, whichever shape it was written in; `null`
 * when the line names no product.
 *
 * @param {unknown} line
 * @returns {{productId: string, quantity: number} | null}
 */
export const supplyOrderLineOnTheWay = (line) => {
  if (line === null || typeof line !== 'object') return null
  const productId = isFullSupplyOrderLine(line) ? line.id : line.productId
  if (typeof productId !== 'string' || productId.length === 0) return null
  const quantity = readOrderQuantity(isFullSupplyOrderLine(line) ? line.orderQuantity : line.quantity)
  return {productId, quantity}
}

/**
 * What SupplyOrderDetailScreen shows as a line's sum: ordered quantity × purchase price, or `null`
 * for a four-key line. That line has no `orderQuantity` and no `purchasePrice` (its `quantity` and
 * `price` are the old dual-write's), so the screen's own formula would render it as a believable
 * 0 ₸ — measured on staging, a 300 ₸ order showed `0 ₸`. `null` renders as '—'.
 *
 * @param {unknown} line
 * @returns {number | null}
 */
export const supplyOrderLineSum = (line) =>
  isFullSupplyOrderLine(line) ? readOrderQuantity(line.orderQuantity) * (line.purchasePrice || 0) : null

/**
 * The order total SupplyOrderDetailScreen shows: the sum of the lines a save would keep
 * (`toSavedOrderItems`), or `null` when any line is a four-key line — a total that leaves a line out
 * is not the order's total, and a save of such an order is refused anyway (`linesUnreadable`).
 *
 * @param {unknown} lines
 * @returns {number | null}
 */
export const supplyOrderFormTotal = (lines) => {
  if (!Array.isArray(lines) || !lines.every(isFullSupplyOrderLine)) return null
  return toSavedOrderItems(lines).items.reduce((sum, line) => sum + (supplyOrderLineSum(line) ?? 0), 0)
}

/**
 * A supply order read from the backend, with its supplier key where the app reads it: `supplierID`.
 *
 * The backend emits the key as `supplierKey` on purpose (ATL-1966). Every released bundle up to
 * v7.1.227 opens SupplyOrderDetailScreen's form only when `suppliersDict[detail.supplierID]`
 * resolves, then reads `storeStock` off every line unguarded — a crash on a four-key line — and the
 * bundles before v7.1.227 also seed a missing date as today and save it over the real one. So a
 * `supplierID` on a Supabase-read order would open that form in those bundles. This bundle handles
 * both, so it
 * — and only it — renames the key. `supplierKey` is removed, so it is never written into Firestore.
 *
 * @template {Record<string, unknown>} T
 * @param {T} order
 * @returns {Omit<T, 'supplierKey'> & {supplierID?: unknown}}
 */
export const withSupplyOrderSupplierKey = (order) => {
  if (order === null || typeof order !== 'object' || !Object.prototype.hasOwnProperty.call(order, 'supplierKey')) {
    return order
  }
  const {supplierKey, ...rest} = order
  return {...rest, supplierID: supplierKey}
}
